import React from "react";
import { motion } from "framer-motion";
import { SparklesIcon, RadarIcon, TruckIcon, LayoutGridIcon, ShieldCheckIcon, HomeIcon, MegaphoneIcon, HeadphonesIcon, MonitorSmartphoneIcon, GemIcon, ArrowRightIcon, CheckIcon, WalletIcon } from "lucide-react";
import { TopBar } from "./TopBar";
import { BRAND, ELM } from "../lib/data";
const HERO = "https://cdn.magicpatterns.com/patterns/generated-images/6a6e43fd-ec77-4f11-b1b2-36a1cd671d9a.jpg";
const HERO_LUXURY = "https://cdn.magicpatterns.com/patterns/generated-images/601bb036-1ee2-4166-842a-5cb98ba36288.jpg";
const BROCHURE = "https://cdn.magicpatterns.com/patterns/generated-images/4b99040b-ef22-480e-8dca-da47f5af99fe.jpg";
const DIRECT_MAIL = "https://cdn.magicpatterns.com/patterns/generated-images/7712a831-0ac8-4aa5-86af-415b347d68e9.jpg";
const SIGNAGE = "https://cdn.magicpatterns.com/patterns/generated-images/74d24684-6eda-4a15-9270-34c9d243c7ff.jpg";
const SOCIAL_PHONE = "https://cdn.magicpatterns.com/patterns/generated-images/859f57e1-bfa9-4d27-91f6-98580a1c97e3.jpg";
const AD_JUST_LISTED = "https://cdn.magicpatterns.com/patterns/generated-images/4eb851a1-a057-496c-aba6-4cc474cfb1d9.jpg";
const AD_OPEN_HOUSE = "https://cdn.magicpatterns.com/patterns/generated-images/91aa69a2-cda6-47af-8aa7-2491a2a0cdda.jpg";
const AD_FEATURE = "https://cdn.magicpatterns.com/patterns/generated-images/dc89f424-70f5-404d-9456-7e8196945870.jpg";
const HOW_IT_WORKS = [{
  icon: SparklesIcon,
  title: "AI ordering",
  body: "Describe what you need in plain language. Our AI builds the right package and routes it instantly."
}, {
  icon: RadarIcon,
  title: "Live tracking",
  body: "Follow every order in real time — from creative design to review to launch."
}, {
  icon: TruckIcon,
  title: "On-time delivery",
  body: "Every package ships against a tracked SLA, with automatic escalation if anything slips."
}, {
  icon: LayoutGridIcon,
  title: "One-stop shop",
  body: "Print, social, digital, luxury, and support — all under one roof, one login, one queue."
}];
const SERVICES = [{
  icon: HomeIcon,
  title: "Listing Concierge",
  body: "Full-service marketing for every new listing — signage, print, social, and web, coordinated for you.",
  image: SIGNAGE
}, {
  icon: MegaphoneIcon,
  title: "Marketing Concierge",
  body: "Campaign strategy and premium creative, tailored to your brand and each property's story.",
  image: BROCHURE
}, {
  icon: MonitorSmartphoneIcon,
  title: "Digital Marketing",
  body: "Targeted ads across Facebook, Instagram, and Google — plus retargeting and lead capture.",
  image: SOCIAL_PHONE
}, {
  icon: GemIcon,
  title: "Luxury Concierge",
  body: "White-glove marketing for high-end and estate properties, from bespoke print to lifestyle films.",
  image: HERO_LUXURY
}, {
  icon: HeadphonesIcon,
  title: "Customer Support",
  body: "A real team on standby, with proactive updates and instant help whenever you need it.",
  image: DIRECT_MAIL
}];
const PRODUCTS = [{
  image: AD_JUST_LISTED,
  label: "Just Listed social ad",
  tag: "Digital"
}, {
  image: BROCHURE,
  label: "Luxury property brochure",
  tag: "Print"
}, {
  image: AD_OPEN_HOUSE,
  label: "Open house announcement",
  tag: "Social"
}, {
  image: DIRECT_MAIL,
  label: "Just-listed direct mail",
  tag: "Print"
}, {
  image: AD_FEATURE,
  label: "Property feature creative",
  tag: "Digital"
}, {
  image: SIGNAGE,
  label: "Branded yard & event signage",
  tag: "Signage"
}];
export function ExclusiveBenefits({
  onHome
}: {
  onHome: () => void;
}) {
  return <div className="min-h-full w-full bg-white text-neutral-900" data-id="element-277" data-mp="ns43cq0r52wr">
      <TopBar onHome={onHome} title="Exclusive Benefits" data-id="element-278" data-mp="nqjbj85tny2l" />

      {/* Hero */}
      <section className="relative overflow-hidden" data-id="element-279" data-mp="rt7ex0brocs6">
        <img src={HERO} alt="Luxury property at golden hour" className="absolute inset-0 h-full w-full object-cover" data-id="element-280" data-mp="wlh2i9pet0ra" />
        <div className="absolute inset-0" style={{
        background: `linear-gradient(90deg, ${BRAND.accent}F7 0%, ${BRAND.accent}E6 45%, ${BRAND.accent}80 72%, ${BRAND.accent}33 100%)`
      }} data-id="element-281" data-mp="2jke74lm5nh" />
        <div className="absolute inset-0 bg-black/10" data-id="element-282" data-mp="3h6qq9ut9udrr" />
        <div className="relative mx-auto max-w-[1400px] px-5 py-20 sm:px-8 sm:py-28" data-id="element-283" data-mp="3h3cksdpb9og3">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="max-w-2xl" data-id="element-284" data-mp="3c4hvqu0r33nj">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-white ring-1 ring-white/30 backdrop-blur" data-id="element-285" data-mp="3m48oor9nxali">
              <SparklesIcon className="h-3.5 w-3.5" data-id="element-286" data-mp="3uxlq6hlslkj7" /> Exclusive to BHHS Fox &amp; Roach Realtors
            </span>
            <h1 className="mt-5 font-heading text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl" data-id="element-287" data-mp="dp8ghhtxuwop">
              Every marketing service your listings deserve — in one place.
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/85" data-id="element-288" data-mp="2fh4sesk0q8k0">
              AI-powered ordering, live tracking, and on-time delivery across print, social, digital, and luxury. One platform, one queue, backed by a dedicated concierge team.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3" data-id="element-289" data-mp="uysw549lpgnt">
              <a href="#services" className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-bold text-neutral-900 transition hover:bg-neutral-100" data-id="element-290" data-mp="v03zx431qoex">
                Explore the services <ArrowRightIcon className="h-4 w-4" data-id="element-291" data-mp="l6538klqrwud" />
              </a>
              <a href="#escrow" className="inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-bold text-white ring-1 ring-white/40 transition hover:bg-white/10" data-id="element-292" data-mp="775fdf61zlz9">
                <WalletIcon className="h-4 w-4" data-id="element-293" data-mp="3tgyh4qyi5gln" /> Paid from escrow
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8 sm:py-20" data-id="element-294" data-mp="1ro1zume01ht3">
        <div className="max-w-2xl" data-id="element-295" data-mp="1wnmuwlq1t60j">
          <p className="text-sm font-bold uppercase tracking-[0.14em]" style={{
          color: BRAND.accentText
        }} data-id="element-296" data-mp="1wmlmyq4bwzn1">The platform</p>
          <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight" data-id="element-297" data-mp="1v82bkzocgyhb">A smarter way to market every listing</h2>
          <p className="mt-3 text-[15px] text-neutral-500" data-id="element-298" data-mp="1q9haky19qart">From the moment you place an order to the day the report is delivered, everything is automated, visible, and on schedule.</p>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-299" data-mp="1gbbfaz9yw2vx">
          {HOW_IT_WORKS.map(({
          icon: Icon,
          title,
          body
        }, i) => <motion.div key={title} initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.05
        }} className="rounded-2xl border border-neutral-200 bg-white p-6" data-id="element-300" data-mp="28xmblxqjjhmx" data-mpi={i}>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl text-white" style={{
            backgroundColor: BRAND.accent
          }} data-id="element-301" data-mp="1z7b4k2bk8sbt">
                <Icon className="h-5 w-5" data-id="element-302" data-mp="2rwqip0yiv1by" />
              </span>
              <h3 className="mt-4 text-base font-semibold" data-id="element-303" data-mp="2rv73ldolqwbr">{title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-neutral-500" data-id="element-304" data-mp="30662naicsbcq">{body}</p>
            </motion.div>)}
        </div>
      </section>

      {/* Services (alternating imagery) */}
      <section id="services" className="border-y border-neutral-200 bg-neutral-50" data-id="element-305" data-mp="395t1ppnm152m">
        <div className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8 sm:py-20" data-id="element-306" data-mp="3e5ovkw2lfg4g">
          <div className="max-w-2xl" data-id="element-307" data-mp="3j48jh20qz1zg">
            <p className="text-sm font-bold uppercase tracking-[0.14em]" style={{
            color: BRAND.accentText
          }} data-id="element-308" data-mp="3j377mvbwqbgo">The services</p>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight" data-id="element-309" data-mp="3n4anvixva9if">Five concierge teams, one seamless platform</h2>
            <p className="mt-3 text-[15px] text-neutral-500" data-id="element-310" data-mp="3rywp4ta8ilos">Order any service on its own, or bundle them — the platform coordinates the handoffs for you.</p>
          </div>

          <div className="mt-12 space-y-10" data-id="element-311" data-mp="5r6uxfezf2uv">
            {SERVICES.map(({
            icon: Icon,
            title,
            body,
            image
          }, i) => {
            const flipped = i % 2 === 1;
            return <motion.div key={title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: "-80px"
            }} className="grid items-center gap-6 rounded-3xl border border-neutral-200 bg-white p-4 sm:p-6 lg:grid-cols-2 lg:gap-10" data-id="element-312" data-mp="vy2lwumo5tca" data-mpi={i}>
                  <div className={`overflow-hidden rounded-2xl ${flipped ? "lg:order-2" : ""}`} data-id="element-313" data-mp="camsozduf8kd">
                    <img src={image} alt={title} className="h-60 w-full object-cover sm:h-72" data-id="element-314" data-mp="8am2khhdt8xh" />
                  </div>
                  <div className={flipped ? "lg:order-1 lg:pr-6" : "lg:pl-6"} data-id="element-315" data-mp="m1zhfi4chim6">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl text-white" style={{
                  backgroundColor: BRAND.accent
                }} data-id="element-316" data-mp="ha90gtawjwls">
                      <Icon className="h-5 w-5" data-id="element-317" data-mp="3po7vnsq1vdy8" />
                    </span>
                    <h3 className="mt-4 font-heading text-2xl font-bold tracking-tight" data-id="element-318" data-mp="3kqojqzgtrd9g">{title}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-neutral-600" data-id="element-319" data-mp="207m6k0hdrxjl">{body}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold" style={{
                  color: BRAND.accentText
                }} data-id="element-320" data-mp="250oyy5af90y7">
                      Order in a few taps <ArrowRightIcon className="h-4 w-4" data-id="element-321" data-mp="1w7by8h648v1k" />
                    </span>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Escrow highlight */}
      <section id="escrow" className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8 sm:py-20" data-id="element-322" data-mp="2nxgdy2ixktwg">
        <div className="overflow-hidden rounded-3xl" style={{
        backgroundColor: BRAND.accent
      }} data-id="element-323" data-mp="29y70ham40dw6">
          <div className="grid items-center gap-8 p-8 sm:p-12 lg:grid-cols-2" data-id="element-324" data-mp="2ex0ceylqzwu8">
            <div data-id="element-325" data-mp="yjk1tpevtxv1">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-white ring-1 ring-white/30" data-id="element-326" data-mp="yj1wm3a8s9az">
                <ShieldCheckIcon className="h-3.5 w-3.5" data-id="element-327" data-mp="nha7y8y42uvm" /> No out-of-pocket cost
              </span>
              <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl" data-id="element-328" data-mp="sgwl6idxwb4f">Every service, paid directly from escrow.</h2>
              <p className="mt-3 max-w-lg text-[15px] leading-relaxed text-white/85" data-id="element-329" data-mp="dpmiuep11pxb">
                Market your listing now and settle at closing. There's nothing to expense, no reimbursements to chase, and no invoices to track — it's all handled through escrow.
              </p>
              <ul className="mt-6 space-y-2.5" data-id="element-330" data-mp="8tyw05y0p8uz">
                {["No upfront payment or personal credit card", "Transparent pricing, itemized at closing", "Approve and launch marketing the same day"].map(point => <li key={point} className="flex items-center gap-2.5 text-sm font-medium text-white" data-id="element-331" data-mp="3vz6mnv3r15sh">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20" data-id="element-332" data-mp="1u5yxltyc39te"><CheckIcon className="h-3 w-3" data-id="element-333" data-mp="1u3w8dc509kpq" /></span>
                    {point}
                  </li>)}
              </ul>
            </div>
            <div className="rounded-2xl bg-white p-5 shadow-xl" data-id="element-334" data-mp="39pznxilkubcn">
              <div className="flex items-center gap-3 border-b border-neutral-100 pb-3" data-id="element-335" data-mp="3odkop5uzbyhe">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl text-white" style={{
                backgroundColor: BRAND.accent
              }} data-id="element-336" data-mp="3jn4ahjjc48tz"><WalletIcon className="h-5 w-5" data-id="element-337" data-mp="18buabm7uc22" /></span>
                <div data-id="element-338" data-mp="1ay0a6tgkji6">
                  <p className="text-sm font-semibold" data-id="element-339" data-mp="3sfoaoof9ylgx">Settlement summary</p>
                  <p className="text-xs text-neutral-500" data-id="element-340" data-mp="3shsd11xizcf3">{ELM.address} · paid at closing</p>
                </div>
              </div>
              <dl className="mt-3 space-y-2.5 text-sm" data-id="element-341" data-mp="67ohixh01l56">
                {[["Listing Concierge package", "$1,450"], ["Digital ad campaign", "$900"], ["Luxury print & signage", "$650"]].map(([k, v]) => <div key={k} className="flex items-center justify-between" data-id="element-342" data-mp="280cmpcd7einl">
                    <dt className="text-neutral-600" data-id="element-343" data-mp="2xnzvgjb202f7">{k}</dt>
                    <dd className="font-semibold text-neutral-900" data-id="element-344" data-mp="2xpjbq9wpmdgz">{v}</dd>
                  </div>)}
                <div className="flex items-center justify-between border-t border-neutral-100 pt-2.5" data-id="element-345" data-mp="31r4ycmz1q64d">
                  <dt className="font-semibold text-neutral-900" data-id="element-346" data-mp="36qraet351gbl">Deducted from escrow</dt>
                  <dd className="font-bold" style={{
                  color: BRAND.accentText
                }} data-id="element-347" data-mp="3bleqc3k2t2ek">$3,000</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* Product examples */}
      <section className="border-t border-neutral-200 bg-neutral-50" data-id="element-348" data-mp="3pf611f9jmsbv">
        <div className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8 sm:py-20" data-id="element-349" data-mp="3pgz8uxvw7ilg">
          <div className="max-w-2xl" data-id="element-350" data-mp="3udhahovxf0tk">
            <p className="text-sm font-bold uppercase tracking-[0.14em]" style={{
            color: BRAND.accentText
          }} data-id="element-351" data-mp="37o9l0gp8yff">Sample work</p>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight" data-id="element-352" data-mp="869bd4blnq5z">A look at what gets delivered</h2>
            <p className="mt-3 text-[15px] text-neutral-500" data-id="element-353" data-mp="1spuvyq32ibvm">Real examples across every category — polished, on-brand, and ready to launch.</p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-354" data-mp="1iqnjs682kna3">
            {PRODUCTS.map((product, i) => <motion.figure key={i} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i % 3 * 0.05
          }} className="group overflow-hidden rounded-2xl border border-neutral-200 bg-white" data-id="element-355" data-mp="1ds2i02d65vjj" data-mpi={i}>
                <div className="relative overflow-hidden" data-id="element-356" data-mp="26gzybzxtli9n">
                  <img src={product.image} alt={product.label} className="aspect-[4/3] w-full object-cover transition duration-500 group-hover:scale-105" data-id="element-357" data-mp="21oq262643lyx" />
                  <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold text-neutral-800 backdrop-blur" data-id="element-358" data-mp="1wokh54uckxpt">{product.tag}</span>
                </div>
                <figcaption className="px-4 py-3 text-sm font-medium text-neutral-700" data-id="element-359" data-mp="2pczkuv813tlu">{product.label}</figcaption>
              </motion.figure>)}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="mx-auto max-w-[1400px] px-5 py-20 text-center sm:px-8" data-id="element-360" data-mp="1zz4wxfkc2074">
        <h2 className="mx-auto max-w-3xl font-heading text-3xl font-bold tracking-tight sm:text-4xl" data-id="element-361" data-mp="2019ciurlgnn8">
          The complete marketing platform, exclusive to Fox &amp; Roach.
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-[15px] text-neutral-500" data-id="element-362" data-mp="2spn8z631bbwo">
          AI ordering, live tracking, on-time delivery, and a full concierge bench — with every dollar handled through escrow.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3" data-id="element-363" data-mp="2nwu66nr4mdn8">
          <button onClick={onHome} className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-bold text-white transition hover:opacity-90" style={{
          backgroundColor: BRAND.accent
        }} data-id="element-364" data-mp="2nur6td43385u">
            See the agent experience <ArrowRightIcon className="h-4 w-4" data-id="element-365" data-mp="14y5xhk2xvazw" />
          </button>
        </div>
        <p className="mt-10 text-xs text-neutral-400" data-id="element-366" data-mp="14vt9oqai26ex">{BRAND.name}</p>
      </section>
    </div>;
}